import {
  Link,
  createFileRoute,
  stripSearchParams,
  type ErrorComponentProps,
} from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { useState } from 'react'

import RouteErrorPage from '@/components/layout/RouteErrorPage'
import ScreenLayout from '@/components/layout/ScreenLayout'
import NotAvailable from '@/components/ui/NotAvailable'
import QueryState from '@/components/ui/QueryState'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { buttonVariants } from '@/components/ui/button-variants'
import { marketNewsService } from '@/features/news/api/marketNewsService'
import { useNewsArticle } from '@/features/news/api/queries'
import { NOT_ADVICE } from '@/features/news/copy'
import NewsMitraNote from '@/features/news/components/NewsMitraNote'
import NewsReaderPanel from '@/features/news/components/NewsReaderPanel'
import NewsShareButton from '@/features/news/components/NewsShareButton'
import { cn } from '@/lib/utils'
import type { NewsArticle, NewsLang } from '@/schemas/market-news'
import { loadNewsArticle } from '@/screens/market/NewsArticleScreen.loader'
import { newsArticleSearchSchema } from '@/screens/market/news-search'

const HEADER = <TitleRow title="News article" />

/**
 * The article's head row (mock `.nw-atitle`): a bordered 20px back square that nudges left on
 * hover, and Share. Known without data, so every state of the page draws it.
 */
function ArticleHead({ lang }: { lang: NewsLang }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <Link
        to="/market/news"
        search={lang === 'ne' ? { lang } : {}}
        aria-label="Back to Market News"
        className={cn(
          'relative inline-flex size-5 shrink-0 items-center justify-center border border-border bg-muted text-foreground',
          'transition-[border-color,translate] hover:border-border-strong hover:motion-safe:-translate-x-(--motion-nudge)',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
          "before:absolute before:-inset-3 before:content-['']",
        )}
      >
        <ChevronLeft className="size-4" strokeWidth={1.8} aria-hidden="true" />
      </Link>
      <NewsShareButton />
    </div>
  )
}

const ARTICLE_SKELETON = (
  <div className="flex flex-col gap-4">
    <Skeleton className="h-50 w-full lg:h-95" />
    <Skeleton className="h-3 w-24 rounded-xs" />
    <Skeleton className="h-9 w-4/5 rounded-xs" />
    <Skeleton className="h-4 w-60 rounded-xs" />
    <div className="flex flex-col gap-3 border-t border-border-subtle pt-4">
      {Array.from({ length: 6 }, (_, index) => (
        <Skeleton key={index} className="h-4 w-full rounded-xs" />
      ))}
    </div>
  </div>
)

function NewsArticlePending() {
  return (
    <ScreenLayout
      pending
      pendingLabel="Opening the story…"
      header={HEADER}
      pageTitle="News article"
    >
      <div className="flex h-9 items-center">
        <Skeleton className="size-5" />
      </div>
      {ARTICLE_SKELETON}
    </ScreenLayout>
  )
}

function NewsArticleError(props: ErrorComponentProps) {
  return (
    <RouteErrorPage
      {...props}
      header={HEADER}
      title="Could not open this story"
      description="The story did not come back. Check your connection and try again."
    />
  )
}

export const Route = createFileRoute('/_app/market/news/article')({
  validateSearch: newsArticleSearchSchema,
  search: { middlewares: [stripSearchParams({ lang: 'en' })] },
  loader: loadNewsArticle,
  pendingComponent: NewsArticlePending,
  errorComponent: NewsArticleError,
  component: NewsArticleScreen,
})

/**
 * The story itself: its photo when it has one, the site, the headline, who wrote it and when,
 * the paragraphs, Mitra's note, and "Read on the source website".
 *
 * That button opens the reader panel on mm-be's frameable copy where there is one, and the news
 * site in a new tab where there is not; the site's own page cannot be framed.
 */
function ArticleBody({ article }: { article: NewsArticle }) {
  const [readerOpen, setReaderOpen] = useState(false)

  return (
    <article className="flex flex-col gap-4" lang={article.lang}>
      {article.heroImageUrl === undefined ? null : (
        <img
          src={article.heroImageUrl}
          alt=""
          className="h-50 w-full bg-muted object-cover lg:h-95"
        />
      )}
      <header className="flex flex-col gap-2 pt-1">
        <p className="text-h-xs text-muted-foreground-subtle">{article.source}</p>
        <h2 className="text-h-section lg:text-h-2xl lg:leading-10">{article.title}</h2>
        <div className="flex flex-col text-body-m">
          <span className="font-semibold text-muted-foreground">{article.byline}</span>
          <span className="text-muted-foreground-subtle">{article.publishedLine}</span>
        </div>
      </header>
      <hr className="border-border-subtle" />
      <div className="flex flex-col gap-4 text-body-l leading-7">
        {article.body.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
      </div>
      {article.note === undefined ? null : <NewsMitraNote note={article.note} />}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        {article.readerUrl === undefined ? (
          <a
            href={article.sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(buttonVariants({ variant: 'secondary', size: 'lg' }), 'max-lg:w-full')}
          >
            Read on the source website
          </a>
        ) : (
          <button
            type="button"
            onClick={() => {
              setReaderOpen(true)
            }}
            className={cn(buttonVariants({ variant: 'secondary', size: 'lg' }), 'max-lg:w-full')}
          >
            Read on the source website
          </button>
        )}
        <p className="text-body-xs text-muted-foreground-subtle">
          {article.readerUrl === undefined
            ? 'Opens the source page in a new tab'
            : 'Opens the source page in a reader panel'}
        </p>
      </div>
      <p className="text-body-xs text-muted-foreground-subtle">{NOT_ADVICE}</p>
      {article.readerUrl === undefined ? null : (
        <NewsReaderPanel
          article={article}
          readerUrl={article.readerUrl}
          open={readerOpen}
          onOpenChange={setReaderOpen}
        />
      )}
    </article>
  )
}

function LiveArticle({ id, lang }: { id: string; lang: NewsLang }) {
  const article = useNewsArticle(id, lang)
  return (
    <QueryState
      query={article}
      skeleton={ARTICLE_SKELETON}
      loadingLabel="Opening the story"
      size="page"
      failed={{
        title: 'Could not open this story',
        description: 'The story did not come back. Check your connection and try again.',
      }}
    >
      {(data) => <ArticleBody article={data} />}
    </QueryState>
  )
}

/**
 * A News story's page (mock `#/market/news/article?id=`). While mm-be has no news endpoint
 * (`marketNewsService.available`) it draws its head and the generic "not live yet" state, and
 * asks nothing (owner ruling 1, 2026-10-28; `asks/nw1.md`).
 */
function NewsArticleScreen() {
  const { id, lang } = Route.useSearch()

  return (
    <ScreenLayout header={HEADER} pageTitle="News article">
      <ArticleHead lang={lang} />
      {marketNewsService.available ? (
        <LiveArticle id={id} lang={lang} />
      ) : (
        <NotAvailable thing="News articles" size="page" />
      )}
    </ScreenLayout>
  )
}
